import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { generatePath } from 'flow/utils/router-utils';
import { useHistory, useParams } from 'react-router-dom';
import getReferralType from '../IncomingCalls/functions/getReferralType';
import Page from '../Page';

const ProfileIncomingCalls = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();
    const { getProfile } = useProfiles(extensionKey);

    const profile = getProfile(profileKey);

    if (!profile) {
        return <Loading />;
    }

    const prepareLandlineText = () => {
        const referralType = getReferralType(profile);

        let secondaryText;

        if (['hangup', 'loop', 'voicemail'].includes(referralType)) {
            secondaryText = i18next.t(
                `settings.incoming_calls.profile.referal.${referralType}`,
            );
        } else if (referralType === 'number') {
            secondaryText = profile.getIn([
                'callForwarding',
                'unconditionalRedirect',
                'forwardingNumber',
                'nationalFormat',
            ]);
        } else {
            secondaryText = i18next.t(
                'settings.incoming_calls.fixed.' + referralType,
            );
        }

        return profile.getIn(['spokenNotification', 'referEntry', 'key'])
            ? i18next.t('settings.incoming_calls.profile.message') +
                  ' + ' +
                  secondaryText
            : secondaryText;
    };

    const busyRedirectType = profile.getIn([
        'callForwarding',
        'busyRedirectNumber',
        'type',
    ]);

    const busyNumber =
        busyRedirectType === 'number'
            ? profile.getIn([
                  'callForwarding',
                  'busyRedirectNumber',
                  'forwardingNumber',
                  'nationalFormat',
              ])
            : null;

    const delayedRedirectType = profile.getIn([
        'callForwarding',
        'delayedRedirectNumber',
        'type',
    ]);

    const delayedNumber =
        delayedRedirectType === 'number'
            ? profile.getIn([
                  'callForwarding',
                  'errorRedirectNumber',
                  'forwardingNumber',
                  'nationalFormat',
              ])
            : null;

    const disconnectedRedirectType = profile.getIn([
        'callForwarding',
        'errorRedirectNumber',
        'type',
    ]);

    const disconnectedNumber =
        disconnectedRedirectType === 'number'
            ? profile.getIn([
                  'callForwarding',
                  'errorRedirectNumber',
                  'forwardingNumber',
                  'nationalFormat',
              ])
            : null;

    const mex = profile.getIn(['parallelCalls', 'letThroughMexCalls']);
    const forwardingDisabled =
        !profile.get('editable') ||
        (profile.getIn(['callForwarding', 'unconditionalRedirect', 'type']) ===
            'voicemail' &&
            !mex);

    return (
        <Page
            header="settings.incoming_calls"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey,
                    }),
                )
            }
        >
            <RouteLink
                disabled={!profile.get('editable')}
                to={generatePath(
                    routes[
                        'settings.profiles.profileKey.incoming-calls.settingType'
                    ],
                    { extensionKey, profileKey, settingType: 'landline' },
                )}
                description={prepareLandlineText()}
            >
                {i18next.t('settings.incoming_calls.fixed')}
            </RouteLink>

            {!isEmptyNullOrUndefined(mex) && (
                <RouteLink
                    disabled={!profile.get('editable')}
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.incoming-calls.settingType'
                        ],
                        { extensionKey, profileKey, settingType: 'mobile' },
                    )}
                    description={i18next.t(
                        profile.getIn(['parallelCalls', 'letThroughMexCalls'])
                            ? 'settings.incoming_calls.mex.mex'
                            : 'settings.incoming_calls.mex.fixed',
                    )}
                >
                    {i18next.t('settings.incoming_calls.mex')}
                </RouteLink>
            )}

            <div className="mt-6">
                <RouteLink
                    disabled={forwardingDisabled}
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.incoming-calls.settingType'
                        ],
                        { extensionKey, profileKey, settingType: 'busy' },
                    )}
                    description={
                        busyNumber ||
                        i18next.t(
                            `settings.incoming_calls.busy.${busyRedirectType}`,
                        )
                    }
                >
                    {i18next.t('settings.incoming_calls.busy')}
                </RouteLink>

                <RouteLink
                    disabled={forwardingDisabled}
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.incoming-calls.settingType'
                        ],
                        { extensionKey, profileKey, settingType: 'delay' },
                    )}
                    description={
                        delayedNumber ||
                        i18next.t(
                            `settings.incoming_calls.delayed.${delayedRedirectType}`,
                        )
                    }
                >
                    {i18next.t('settings.incoming_calls.delayed')}
                </RouteLink>

                <RouteLink
                    disabled={forwardingDisabled}
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.incoming-calls.settingType'
                        ],
                        {
                            extensionKey,
                            profileKey,
                            settingType: 'disconnected',
                        },
                    )}
                    description={
                        disconnectedNumber ||
                        i18next.t(
                            `settings.incoming_calls.delayed.${disconnectedRedirectType}`,
                        )
                    }
                >
                    {i18next.t('settings.incoming_calls.disconnected')}
                </RouteLink>
            </div>
        </Page>
    );
};

export default ProfileIncomingCalls;
